import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchCompletedFolders } from "@store/thunks/dashboardThunk";
import { logoutUser } from "@store/thunks/authAction";
import { setSelectedTargetId, setSelectedFolderId } from "@store/slices/dashboardSlice";
import { useDebounce } from "react-use";
import { restrictSpecialCharacters } from "@/utils/constants";

const DevnagriValidatedFoldersTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { completedFolders, selectedProjectId } = useSelector((store) => store.dashboard);
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [allFolders, setAllFolders] = useState([]); // Store all fetched folders
  const inputRef = useRef(null);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  // Debounce search input
  useDebounce(
    () => {
      setDebouncedSearchQuery(inputValue.toLowerCase());
      setPageIndex(0); // Reset to first page on search change
      setHasNextPage(true); // Assume more pages until filtering
    },
    300,
    [inputValue]
  );

  useEffect(() => {
    if (selectedProjectId) {
      dispatch(fetchCompletedFolders("Devnagri", selectedProjectId))
        .then((res) => {
          if (Array.isArray(res?.data)) {
            setAllFolders(res.data); // Store all folders
            setHasNextPage(res.data.length > pageSize);
          } else {
            setAllFolders([]);
            setHasNextPage(false);
          }
        });
    } else {
      navigate("/superAdmin/devnagri/approved-projects");
    }
  }, [dispatch, navigate, selectedProjectId]);

  // Client-side filtering
  const filteredFolders = useMemo(() => {
    return debouncedSearchQuery
      ? allFolders.filter(
        (folder) =>
          folder.folderName?.toLowerCase().includes(debouncedSearchQuery) ||
          String(folder.fileCount)?.includes(debouncedSearchQuery)
      )
      : allFolders;
  }, [allFolders, debouncedSearchQuery]);

  // Compute paginated data
  const paginatedFolders = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return filteredFolders.slice(start, end);
  }, [filteredFolders, pageIndex, pageSize]);

  // Update hasNextPage based on filtered data
  useEffect(() => {
    setHasNextPage((pageIndex + 1) * pageSize < filteredFolders.length);
  }, [filteredFolders, pageIndex, pageSize]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };


  const handleClickBack = () => {
    navigate(-1);
  };

  const handleClickHome = () => {
    navigate("/superAdmin");
  };

  const handleFolderClick = ({ row }) => {
    console.log("Row data:", row);
    const fileId = row.folderId || (row.original && row.original.folderId);

    if (!fileId) {
      console.error("Folder ID not found in row data:", row);
      alert("Error: Could not determine Folder ID");
      return;
    }

    dispatch(setSelectedFolderId(fileId));
    dispatch(setSelectedTargetId(row.targetId));
    navigate("/superAdmin/devnagri/approved-projects/folders/approved-files");
  };

  const sentenceColumns = [
    {
      header: "Sr. No.",
      cell: ({ row }) => {
        return <div className="text-center text-sm font-['Semantic/Strong']">{pageIndex * pageSize + row.index + 1}</div>;
      },
      cellClassName: "text-center text-sm",
    },
    {
      header: "Folder Name",
      accessorKey: "folderName",
      cellClassName: "cursor-pointer hover:text-blue-600",
      onClick: handleFolderClick,
      cell: ({ row }) => (
        <div className="text-[#ec7026] hover:text-[#d85d1a] focus:underline">
          {row.original.folderName}
        </div>
      ),
    },
    {
      header: "File Count",
      accessorKey: "fileCount",
      cell: ({ row }) => (
        <div className="text-center font-['Semantic/Strong']">
          {row.original?.fileCount !== undefined ? row.original.fileCount : "N/A"}
        </div>
      ),
      cellClassName: "text-center",
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50 mt-14">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex flex-col">
              <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                <span className="text-[#ec7026] shadow-sm">Devnagri</span> Completed Folders
              </h1>
              <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                View and manage completed GA Solutions projects
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
              <button
                onClick={handleClickHome}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span>Home</span>
              </button>

              <button
                onClick={handleClickBack}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>
      </div>


      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="flex justify-end mb-6">
          <div className="relative w-full sm:w-64">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search folders..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
              value={inputValue}
              onChange={handleInputChange}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            {paginatedFolders.length === 0 ? (
              <div className="p-6">
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
                  <p className="font-['Semantic/Strong']">No folders found.</p>
                  <p className="font-['Semantic/Strong'] mt-2">
                    No completed Devnagri folders match the search criteria.
                  </p>
                </div>
              </div>
            ) : (
              <GenericTable
                data={paginatedFolders}
                columns={sentenceColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No completed Devnagri folders available"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevnagriValidatedFoldersTable;